import { useMemo, useState } from "react";
import { Copy, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const ZONES = [
  "France métropolitaine",
  "France métropolitaine et départements d’outre-mer",
  "Autre zone (à préciser)",
];

const GARANTIES = [
  { id: "rcd", label: "Responsabilité civile décennale" },
  { id: "rcp", label: "Responsabilité civile professionnelle" },
];

const inputCls =
  "w-full rounded-none border-b-2 border-ink/40 bg-transparent py-2.5 text-base outline-none transition-colors placeholder:text-ink/35 focus:border-accent";

const frDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

export default function MentionGenerator() {
  const [form, setForm] = useState({
    assureur: "",
    coordonnees: "",
    contrat: "",
    intermediaire: "",
    zone: ZONES[0],
    autreZone: "",
    debut: "",
    fin: "",
    rcd: true,
    rcp: true,
  });
  const [copied, setCopied] = useState(false);
  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setCopied(false);
  };

  const text = useMemo(() => {
    const garanties = GARANTIES.filter((g) => form[g.id]).map((g) => g.label.toLowerCase());
    const couverture = garanties.length ? garanties.join(" et ") : "[garanties souscrites]";
    const zone = form.zone === ZONES[2] ? form.autreZone.trim() || "[zone géographique du contrat]" : form.zone;
    const validite = form.debut && form.fin ? ` (période de validité : du ${frDate(form.debut)} au ${frDate(form.fin)})` : "";
    const intermediaire = form.intermediaire.trim() ? `, par l’intermédiaire de ${form.intermediaire.trim()}` : "";
    return (
      `Assurance professionnelle : ${couverture} souscrite${garanties.length > 1 ? "s" : ""} auprès de ${form.assureur.trim() || "[nom de l’assureur]"}` +
      ` (${form.coordonnees.trim() || "[adresse ou téléphone de l’assureur]"}), contrat n° ${form.contrat.trim() || "[numéro de contrat]"}${intermediaire}. ` +
      `Couverture géographique : ${zone}. Attestation d’assurance jointe au présent document${validite}.`
    );
  }, [form]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.getElementById("mention-output");
      area?.select();
      document.execCommand?.("copy");
    }
    setCopied(true);
    trackEvent("tool_mention_copied");
  };

  return (
    <div className="my-6 border-2 border-ink bg-white p-5 sm:p-8 shadow-brutal" data-testid="mention-generator">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="mg-assureur" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Assureur (compagnie)</label>
          <input id="mg-assureur" className={inputCls} placeholder="Nom figurant sur l’attestation" value={form.assureur} onChange={(e) => set("assureur", e.target.value)} />
        </div>
        <div>
          <label htmlFor="mg-contrat" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">N° de contrat</label>
          <input id="mg-contrat" className={inputCls} placeholder="Ex. 123456789" value={form.contrat} onChange={(e) => set("contrat", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="mg-coordonnees" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Coordonnées de l’assureur</label>
          <input id="mg-coordonnees" className={inputCls} placeholder="Adresse, téléphone ou site internet" value={form.coordonnees} onChange={(e) => set("coordonnees", e.target.value)} />
        </div>
        <div>
          <label htmlFor="mg-zone" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Couverture géographique</label>
          <select id="mg-zone" className={`${inputCls} cursor-pointer`} value={form.zone} onChange={(e) => set("zone", e.target.value)}>
            {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          {form.zone === ZONES[2] && (
            <input aria-label="Zone géographique du contrat" className={`${inputCls} mt-2`} placeholder="Zone indiquée au contrat" value={form.autreZone} onChange={(e) => set("autreZone", e.target.value)} />
          )}
        </div>
        <div>
          <label htmlFor="mg-intermediaire" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Intermédiaire (facultatif)</label>
          <input id="mg-intermediaire" className={inputCls} placeholder="Courtier ou agent" value={form.intermediaire} onChange={(e) => set("intermediaire", e.target.value)} />
        </div>
        <div>
          <label htmlFor="mg-debut" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Attestation valable du</label>
          <input id="mg-debut" type="date" className={inputCls} value={form.debut} onChange={(e) => set("debut", e.target.value)} />
        </div>
        <div>
          <label htmlFor="mg-fin" className="mb-1 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">au</label>
          <input id="mg-fin" type="date" className={inputCls} value={form.fin} onChange={(e) => set("fin", e.target.value)} />
        </div>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Garanties souscrites</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {GARANTIES.map((g) => (
              <label key={g.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-[#C73A16]" checked={form[g.id]} onChange={(e) => set(g.id, e.target.checked)} />
                {g.label}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <label htmlFor="mention-output" className="mt-8 mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Texte à copier dans vos devis et factures</label>
      <textarea id="mention-output" readOnly rows={5} value={text} className="w-full resize-y border-2 border-ink/30 bg-paper p-4 text-sm leading-relaxed" />
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button type="button" onClick={copy} className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper transition-colors hover:bg-accent hover:border-accent">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Texte copié" : "Copier le texte"}
        </button>
        <p className="text-xs leading-relaxed text-ink/60 max-w-md">Recopiez les informations de votre attestation à l’identique. Cet encart complète l’attestation jointe, il ne la remplace pas.</p>
      </div>
    </div>
  );
}
